import api from '@services/api.service'
import { createAsyncThunk, EntityId } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import { DjangoErrorResponse } from '@store/slices/generic.slice'
import { toast } from 'react-toastify'
import { AISlotModel, aiSlotUrl } from '@store/slices/aiSlot.slice'

export const narrateSlot = createAsyncThunk(
  'slot/narrate',
  async (props: {
    playlistId: EntityId
    slotId: EntityId
    generateSpeech?: boolean
  }) => {
    try {
      const params: any = { playlistId: props.playlistId }
      if (props.generateSpeech !== undefined)
        params.generateSpeech = props.generateSpeech
      const response = await api.get(`${aiSlotUrl}${props.slotId}/narrate/`, {
        params,
      })
      return response.data as EntityId
    } catch (error) {
      const axiosError = error as AxiosError
      if (axiosError.response) {
        const serverError = axiosError.response.data as DjangoErrorResponse
        toast.error(
          `${serverError.message || 'Nepodařilo se vygenerovat data pro slot'}`
        )
      } else {
        toast.error('Nepodařilo se vygenerovat data pro slot')
      }
      throw axiosError
    }
  }
)

export const transcribeSlot = createAsyncThunk(
  'slot/transcribe',
  async (props: { playlistId: EntityId; slotId: EntityId }) => {
    try {
      const params: any = { playlistId: props.playlistId }
      const response = await api.get(
        `${aiSlotUrl}${props.slotId}/transcribe/`,
        {
          params,
        }
      )
      return response.data as EntityId
    } catch (error) {
      const axiosError = error as AxiosError
      if (axiosError.response) {
        const serverError = axiosError.response.data as DjangoErrorResponse
        toast.error(
          `${serverError.message || 'Nepodařilo se vygenerovat dialog pro slot'}`
        )
      } else {
        toast.error('Nepodařilo se vygenerovat dialog pro slot')
      }
      throw axiosError
    }
  }
)

export const createNarrationRegions = createAsyncThunk(
  'slot/createNarrationRegions',
  async (props: { slotId: EntityId }) => {
    try {
      const response = await api.post(
        `${aiSlotUrl}${props.slotId}/createNarrationRegions/`
      )
      return response.data as AISlotModel
    } catch (error) {
      const axiosError = error as AxiosError
      if (axiosError.response) {
        const serverError = axiosError.response.data as DjangoErrorResponse
        toast.error(
          `${serverError.message || 'Nepodařilo se ulozit nový region pro narace'}`
        )
      } else {
        toast.error('Nepodařilo se ulozit nový region pro narace')
      }
      throw axiosError
    }
  }
)

export const generateSlotOutline = createAsyncThunk(
  'slot/outline',
  async (props: { playlistId: EntityId; slotId: EntityId }) => {
    try {
      const params: any = { playlistId: props.playlistId }
      const response = await api.get(`${aiSlotUrl}${props.slotId}/outline/`, {
        params,
      })
      return response.data as EntityId
    } catch (error) {
      const axiosError = error as AxiosError
      if (axiosError.response) {
        const serverError = axiosError.response.data as DjangoErrorResponse
        toast.error(
          `${serverError.message || 'Nepodařilo se vygenerovat outline pro slot'}`
        )
      } else {
        toast.error('Nepodařilo se vygenerovat outline pro slot')
      }
      throw axiosError
    }
  }
)

export const changeSlotDefaultVoice = createAsyncThunk(
  'slot/changeDefaultVoice',
  async (props: { slotId: EntityId; voiceId: EntityId }) => {
    try {
      const response = await api.post(
        `${aiSlotUrl}${props.slotId}/changeDefaultVoice/`,
        { voiceId: props.voiceId }
      )
      return response.data as AISlotModel
    } catch (error) {
      const axiosError = error as AxiosError
      if (axiosError.response) {
        const serverError = axiosError.response.data as DjangoErrorResponse
        toast.error(
          `${serverError.message || 'Nepodařilo se aktualizovat slot'}`
        )
      } else {
        toast.error('Nepodařilo se aktualizovat slot')
      }
      throw axiosError
    }
  }
)

export const setSlotTemplate = createAsyncThunk(
  'slot/setSlotTemplate',
  async (props: { slotId: EntityId; templateId: EntityId }) => {
    try {
      const response = await api.post(
        `${aiSlotUrl}${props.slotId}/setTemplate/`,
        { templateId: props.templateId }
      )
      return response.data as AISlotModel
    } catch (error) {
      const axiosError = error as AxiosError
      if (axiosError.response) {
        const serverError = axiosError.response.data as DjangoErrorResponse
        toast.error(
          `${serverError.message || 'Nepodařilo se aktualizovat slot'}`
        )
      } else {
        toast.error('Nepodařilo se aktualizovat slot')
      }
      throw axiosError
    }
  }
)

export const setSlotBgAudio = createAsyncThunk(
  'slot/setSlotBgAudio',
  async (props: { slotId: EntityId; bgAudioId: EntityId }) => {
    try {
      const response = await api.post(
        `${aiSlotUrl}${props.slotId}/setBgAudio/`,
        { audioId: props.bgAudioId }
      )
      return response.data as AISlotModel
    } catch (error) {
      const axiosError = error as AxiosError
      if (axiosError.response) {
        const serverError = axiosError.response.data as DjangoErrorResponse
        toast.error(
          `${serverError.message || 'Nepodařilo se aktualizovat slot'}`
        )
      } else {
        toast.error('Nepodařilo se aktualizovat slot')
      }
      throw axiosError
    }
  }
)
export const publishUpdatedSlot = createAsyncThunk(
  'slot/publishUpdated',
  async (slotId: EntityId) => {
    try {
      const response = await api.get(`${aiSlotUrl}${slotId}/publishUpdated/`)
      return response.data as EntityId
    } catch (error) {
      const axiosError = error as AxiosError
      if (axiosError.response) {
        const serverError = axiosError.response.data as DjangoErrorResponse
        toast.error(
          `${serverError.message || 'Nepodařilo se publikovat audia pro Slot'}`
        )
      } else {
        toast.error('Nepodařilo se publikovat audia pro Slot')
      }
      throw axiosError
    }
  }
)

export const publishAllSlot = createAsyncThunk(
  'slot/publishAll',
  async (slotId: EntityId) => {
    try {
      const response = await api.get(`${aiSlotUrl}${slotId}/publishAll/`)
      return response.data as EntityId
    } catch (error) {
      const axiosError = error as AxiosError
      if (axiosError.response) {
        const serverError = axiosError.response.data as DjangoErrorResponse
        toast.error(
          `${serverError.message || 'Nepodařilo se publikovat audia pro Slot'}`
        )
      } else {
        toast.error('Nepodařilo se publikovat audia pro Slot')
      }
      throw axiosError
    }
  }
)
